import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExt from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import ImageExt from "@tiptap/extension-image";
import LinkExt from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import Youtube from "@tiptap/extension-youtube";
import {
  Plus,
  Search,
  FileText,
  Trash2,
  Loader2,
  Save,
  Eye,
  EyeOff,
  ArrowLeft,
  ExternalLink,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link2,
  ImageIcon,
  Minus,
  Undo2,
  Redo2,
  Quote,
  Globe,
  Copy,
  CheckSquare,
  TableIcon,
  Video,
  Code,
  ArrowUpCircle,
  ArrowDownCircle,
} from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
const GrapesEditor = lazy(() => import("../components/GrapesEditor"));
import { colors, fonts } from "../styles/theme";
import { Button } from "../components/ui";
import FormBuilder, { getDefaultFormConfig, LANDING_TEMPLATES } from "../components/FormBuilder";
import {
  fetchWebPages,
  fetchWebPage,
  createWebPage,
  updateWebPage,
  deleteWebPage,
  uploadImage,
  fetchFormSubmissions,
  publishStatic,
  fmtDate,
} from "../services/api";

const STATUS_BADGES = {
  draft: { label: "Draft", color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
  published: {
    label: "Published",
    color: "#10b981",
    bg: "rgba(16,185,129,0.10)",
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.draft;
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
      }}
    >
      {cfg.label}
    </span>
  );
}

/* ── Toolbar for TipTap ──────────────────────────────────── */
function EditorToolbar({ editor }) {
  if (!editor) return null;

  const addImage = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const result = await uploadImage(file);
        if (result?.url)
          editor.chain().focus().setImage({ src: result.url }).run();
      } catch (err) {
        alert("Image upload failed: " + err.message);
      }
    };
    input.click();
  }, [editor]);

  const addLink = useCallback(() => {
    const url = window.prompt(
      "URL:",
      editor.getAttributes("link").href || "https://",
    );
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url, target: "_blank" })
        .run();
    }
  }, [editor]);

  const btn = (active, onClick, Icon, title) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        background: active ? "rgba(255,255,255,0.12)" : "transparent",
        border: "none",
        padding: "6px 8px",
        borderRadius: 4,
        cursor: "pointer",
        color: active ? "#fff" : colors.textSecondary,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Icon size={16} />
    </button>
  );

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        padding: "6px 8px",
        background: colors.bgCard,
        borderBottom: `1px solid ${colors.border}`,
        borderRadius: "8px 8px 0 0",
      }}
    >
      {btn(
        editor.isActive("bold"),
        () => editor.chain().focus().toggleBold().run(),
        Bold,
        "Bold",
      )}
      {btn(
        editor.isActive("italic"),
        () => editor.chain().focus().toggleItalic().run(),
        Italic,
        "Italic",
      )}
      {btn(
        editor.isActive("underline"),
        () => editor.chain().focus().toggleUnderline().run(),
        Underline,
        "Underline",
      )}
      {btn(
        editor.isActive("strike"),
        () => editor.chain().focus().toggleStrike().run(),
        Strikethrough,
        "Strikethrough",
      )}
      <span style={{ width: 1, background: colors.border, margin: "0 4px" }} />
      {btn(
        editor.isActive({ textAlign: "left" }),
        () => editor.chain().focus().setTextAlign("left").run(),
        AlignLeft,
        "Align left",
      )}
      {btn(
        editor.isActive({ textAlign: "center" }),
        () => editor.chain().focus().setTextAlign("center").run(),
        AlignCenter,
        "Center",
      )}
      {btn(
        editor.isActive({ textAlign: "right" }),
        () => editor.chain().focus().setTextAlign("right").run(),
        AlignRight,
        "Align right",
      )}
      <span style={{ width: 1, background: colors.border, margin: "0 4px" }} />
      {btn(
        editor.isActive("bulletList"),
        () => editor.chain().focus().toggleBulletList().run(),
        List,
        "Bullet list",
      )}
      {btn(
        editor.isActive("orderedList"),
        () => editor.chain().focus().toggleOrderedList().run(),
        ListOrdered,
        "Ordered list",
      )}
      {btn(
        editor.isActive("blockquote"),
        () => editor.chain().focus().toggleBlockquote().run(),
        Quote,
        "Blockquote",
      )}
      {btn(
        false,
        () => editor.chain().focus().setHorizontalRule().run(),
        Minus,
        "Horizontal rule",
      )}
      <span style={{ width: 1, background: colors.border, margin: "0 4px" }} />
      {btn(editor.isActive("link"), addLink, Link2, "Link")}
      {btn(false, addImage, ImageIcon, "Insert image")}
      {btn(
        editor.isActive("codeBlock"),
        () => editor.chain().focus().toggleCodeBlock().run(),
        Code,
        "Code block",
      )}
      <span style={{ width: 1, background: colors.border, margin: "0 4px" }} />
      {btn(
        false,
        () =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run(),
        TableIcon,
        "Insert table",
      )}
      {btn(
        false,
        () => {
          const url = window.prompt("YouTube or Vimeo URL:");
          if (url) editor.commands.setYoutubeVideo({ src: url });
        },
        Video,
        "Embed video",
      )}
      {editor.isActive("table") && (
        <>
          <button
            type="button"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            title="Add column"
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              borderRadius: 4,
              cursor: "pointer",
              color: colors.textSecondary,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            +Col
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            title="Add row"
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              borderRadius: 4,
              cursor: "pointer",
              color: colors.textSecondary,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            +Row
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            title="Delete column"
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              borderRadius: 4,
              cursor: "pointer",
              color: "#ef4444",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            -Col
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteRow().run()}
            title="Delete row"
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              borderRadius: 4,
              cursor: "pointer",
              color: "#ef4444",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            -Row
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteTable().run()}
            title="Delete table"
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              borderRadius: 4,
              cursor: "pointer",
              color: "#ef4444",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Del Table
          </button>
        </>
      )}
      <span style={{ width: 1, background: colors.border, margin: "0 4px" }} />
      {btn(false, () => editor.chain().focus().undo().run(), Undo2, "Undo")}
      {btn(false, () => editor.chain().focus().redo().run(), Redo2, "Redo")}
      <span style={{ width: 1, background: colors.border, margin: "0 4px" }} />
      <select
        onChange={(e) => {
          const v = e.target.value;
          if (v === "p") editor.chain().focus().setParagraph().run();
          else
            editor
              .chain()
              .focus()
              .toggleHeading({ level: parseInt(v) })
              .run();
        }}
        value={
          editor.isActive("heading", { level: 1 })
            ? "1"
            : editor.isActive("heading", { level: 2 })
              ? "2"
              : editor.isActive("heading", { level: 3 })
                ? "3"
                : "p"
        }
        style={{
          background: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          borderRadius: 4,
          padding: "3px 6px",
          fontSize: 12,
          cursor: "pointer",
        }}
      >
        <option value="p">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </select>
    </div>
  );
}

/* ── Page Editor ─────────────────────────────────────────── */
function PageEditor({ page, onSave, onBack, saving }) {
  const [title, setTitle] = useState(page?.title || "");
  const [slug, setSlug] = useState(page?.slug || "");
  const [metaDescription, setMetaDescription] = useState(
    page?.metaDescription || "",
  );
  const [status, setStatus] = useState(page?.status || "draft");
  const [canonicalUrl, setCanonicalUrl] = useState(page?.canonicalUrl || "");
  const [featuredImage, setFeaturedImage] = useState(page?.featuredImage || "");
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [slugManual, setSlugManual] = useState(!!page?.slug);
  const [wordCount, setWordCount] = useState(0);
  const [preview, setPreview] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | "saving" | "saved" | "error"
  const autoSaveTimer = useRef(null);
  const editorRef = useRef(null);
  const tenant = useTenant();
  const useGrapes = tenant.id === "l30";
  const gjsDataRef = useRef({
    gjsProjectData: page?.gjsProjectData || null,
    gjsHtml: page?.bodyHtml || "",
    gjsCss: page?.gjsCss || "",
    bodyHtml: page?.bodyHtml || "",
  });

  const isLanding = page?.pageType === "landing";
  const [formConfig, setFormConfig] = useState(
    page?.formConfig || (isLanding ? getDefaultFormConfig() : null),
  );
  const [submissions, setSubmissions] = useState([]);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsLoading, setSubsLoading] = useState(false);
  const [showSubs, setShowSubs] = useState(false);

  const loadSubmissions = useCallback(async () => {
    if (!page?.id || !isLanding) return;
    setSubsLoading(true);
    try {
      const result = await fetchFormSubmissions(page.id);
      setSubmissions(result.submissions || []);
      setSubsTotal(result.total || 0);
    } catch (_) { /* best effort */ }
    finally { setSubsLoading(false); }
  }, [page?.id, isLanding]);

  const countWords = (text) => text.trim().split(/\s+/).filter(Boolean).length;

  const doAutoSave = useCallback(() => {
    if (!page?.id || !title.trim() || !slug.trim()) return;
    setAutoSaveStatus("saving");
    const gjs = gjsDataRef.current;
    const data = {
      title: title.trim(),
      slug: slug.trim(),
      bodyHtml: useGrapes ? (gjs.bodyHtml || "") : (editorRef.current?.getHTML() || ""),
      ...(useGrapes ? { gjsProjectData: gjs.gjsProjectData, gjsCss: gjs.gjsCss || "" } : {}),
      metaDescription: metaDescription.trim(),
      canonicalUrl: canonicalUrl.trim(),
      featuredImage,
      status,
      pageType: page.pageType || "content",
      _silent: true,
    };
    if (isLanding && formConfig) data.formConfig = formConfig;
    onSave(data)
      .then(() => {
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus(null), 3000);
      })
      .catch(() => {
        setAutoSaveStatus("error");
      });
  }, [
    page?.id,
    title,
    slug,
    metaDescription,
    canonicalUrl,
    featuredImage,
    status,
    formConfig,
    isLanding,
    onSave,
  ]);

  const scheduleAutoSave = useCallback(() => {
    if (!page?.id) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(doAutoSave, 3000);
  }, [page?.id, doAutoSave]);

  // Cleanup timer on unmount
  useEffect(
    () => () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    },
    [],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false, underline: false }),
      UnderlineExt,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      ImageExt.configure({ inline: false, allowBase64: false }),
      LinkExt.configure({ openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Youtube.configure({
        inline: false,
        ccLanguage: "en",
        interfaceLanguage: "en",
      }),
      Placeholder.configure({
        placeholder: "Start writing your page content...",
      }),
    ],
    content: page?.bodyHtml || "",
    onCreate: ({ editor: e }) => { editorRef.current = e; setWordCount(countWords(e.getText())); },
    onUpdate: ({ editor: e }) => {
      setWordCount(countWords(e.getText()));
      scheduleAutoSave();
    },
  });

  /* ── Drag-and-drop image upload ─────────────────────── */
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes("Files")) setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      dragCounter.current = 0;
      const files = Array.from(e.dataTransfer.files).filter((f) =>
        f.type.startsWith("image/"),
      );
      if (!files.length || !editor) return;
      for (const file of files) {
        try {
          const result = await uploadImage(file);
          if (result?.url)
            editor.chain().focus().setImage({ src: result.url }).run();
        } catch (err) {
          alert("Image upload failed: " + err.message);
        }
      }
    },
    [editor],
  );

  const handleGrapesUpdate = useCallback((data) => {
    gjsDataRef.current = data;
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  // Auto-save when title, slug, metaDescription, status, or formConfig change
  useEffect(() => {
    if (page?.id) scheduleAutoSave();
  }, [title, slug, metaDescription, canonicalUrl, featuredImage, status, formConfig]);

  // Auto-generate slug from title (only if user hasn't manually edited the slug)
  useEffect(() => {
    if (!slugManual && title && !page?.id) {
      setSlug(
        title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
      );
    }
  }, [title, slugManual, page?.id]);

  const handleSave = () => {
    if (!title.trim()) return alert("Title is required");
    if (!slug.trim()) return alert("Slug is required");
    const gjs = gjsDataRef.current;
    const data = {
      title: title.trim(),
      slug: slug.trim(),
      bodyHtml: useGrapes ? (gjs.bodyHtml || "") : (editor?.getHTML() || ""),
      ...(useGrapes ? { gjsProjectData: gjs.gjsProjectData, gjsCss: gjs.gjsCss || "" } : {}),
      metaDescription: metaDescription.trim(),
      canonicalUrl: canonicalUrl.trim(),
      featuredImage,
      status,
      pageType: page?.pageType || "content",
    };
    if (isLanding && formConfig) data.formConfig = formConfig;
    onSave(data);
  };

  const publicUrl = `/api/page/${slug}`;

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    color: colors.text,
    fontSize: 14,
    fontFamily: fonts.body,
    outline: "none",
  };

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: colors.textSecondary,
              display: "flex",
              padding: 4,
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <h2
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: colors.text,
              fontFamily: fonts.heading,
              margin: 0,
            }}
          >
            {page?.id ? "Edit" : "New"} {isLanding ? "Landing Page" : "Page"}
          </h2>
          {page?.id && <StatusBadge status={status} />}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setPreview(!preview)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${preview ? colors.accent : colors.border}`,
              background: preview ? colors.accentDim : "transparent",
              color: preview ? colors.accent : colors.textSecondary,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {preview ? (
              <>
                <Eye size={14} /> Preview
              </>
            ) : (
              <>
                <Eye size={14} /> Preview
              </>
            )}
          </button>
          {page?.id && (
            <button
              onClick={() =>
                setStatus(status === "published" ? "draft" : "published")
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px solid ${status === "published" ? "#10b981" : colors.border}`,
                background:
                  status === "published"
                    ? "rgba(16,185,129,0.08)"
                    : "transparent",
                color:
                  status === "published" ? "#10b981" : colors.textSecondary,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {status === "published" ? (
                <>
                  <EyeOff size={14} /> Unpublish
                </>
              ) : (
                <>
                  <Eye size={14} /> Publish
                </>
              )}
            </button>
          )}
          {page?.id && status === "published" && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.textSecondary,
                fontSize: 13,
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              <ExternalLink size={14} /> View
            </a>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            {saving ? (
              <Loader2
                size={14}
                className="spin"
              />
            ) : (
              <Save size={14} />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Page fields */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: 6,
            }}
          >
            Title
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Page title"
            style={inputStyle}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: 6,
            }}
          >
            Slug
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: colors.textSecondary, fontSize: 13 }}>
              /page/
            </span>
            <input
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugManual(true);
              }}
              placeholder="page-slug"
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 16,
          marginBottom: 20,
          alignItems: "end",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: 6,
            }}
          >
            Meta Description
          </label>
          <input
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            placeholder="Brief description for search engines"
            style={inputStyle}
            maxLength={160}
          />
          <div
            style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}
          >
            {metaDescription.length}/160
          </div>
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: 6,
            }}
          >
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ ...inputStyle, width: 140, cursor: "pointer" }}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
      </div>

      {/* SEO Preview */}
      <div
        style={{
          marginBottom: 20,
          padding: "16px 20px",
          borderRadius: 8,
          background: "#fff",
          border: `1px solid ${colors.border}`,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: colors.textSecondary,
            marginBottom: 10,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Search Engine Preview
        </div>
        <div
          style={{
            fontSize: 18,
            color: "#1a0dab",
            fontFamily: "Arial, sans-serif",
            lineHeight: 1.3,
            marginBottom: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title || "Page Title"}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#006621",
            fontFamily: "Arial, sans-serif",
            marginBottom: 2,
          }}
        >
          {window.location.origin}/page/{slug || "page-slug"}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#545454",
            fontFamily: "Arial, sans-serif",
            lineHeight: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {metaDescription ||
            "Add a meta description to control how this page appears in search results."}
        </div>
      </div>

      {/* Copyable public URL */}
      {page?.id && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 20,
            padding: "8px 12px",
            borderRadius: 8,
            background: "rgba(37,99,235,0.06)",
            border: "1px solid rgba(37,99,235,0.15)",
          }}
        >
          <Globe size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: colors.textSecondary }}>
            Public URL:
          </span>
          <code
            style={{
              fontSize: 12,
              color: "#2563eb",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {window.location.origin}
            {publicUrl}
          </code>
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.origin + publicUrl);
            }}
            title="Copy URL"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#2563eb",
              display: "flex",
              padding: 4,
            }}
          >
            <Copy size={14} />
          </button>
        </div>
      )}

      {/* Canonical URL */}
      <div style={{ marginBottom: 20 }}>
        <label
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: colors.textSecondary,
            display: "block",
            marginBottom: 6,
          }}
        >
          Canonical URL{" "}
          <span style={{ fontWeight: 400 }}>
            (optional — set if this content exists elsewhere)
          </span>
        </label>
        <input
          value={canonicalUrl}
          onChange={(e) => setCanonicalUrl(e.target.value)}
          placeholder="https://example.com/original-page"
          style={inputStyle}
        />
        {canonicalUrl && (
          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>
            Search engines will treat <strong>{canonicalUrl}</strong> as the
            primary version of this page.
          </div>
        )}
      </div>

      {/* Featured Image */}
      <div style={{ marginBottom: 20 }}>
        <label
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: colors.textSecondary,
            display: "block",
            marginBottom: 6,
          }}
        >
          Featured Image
        </label>
        {featuredImage ? (
          <div
            style={{
              position: "relative",
              display: "inline-block",
              borderRadius: 8,
              overflow: "hidden",
              border: `1px solid ${colors.border}`,
            }}
          >
            <img
              src={featuredImage}
              alt="Featured"
              style={{
                maxWidth: "100%",
                maxHeight: 220,
                display: "block",
                objectFit: "cover",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                gap: 4,
              }}
            >
              <button
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  input.onchange = async () => {
                    const file = input.files?.[0];
                    if (!file) return;
                    setUploadingFeatured(true);
                    try {
                      const result = await uploadImage(file);
                      if (result?.url) setFeaturedImage(result.url);
                    } catch (err) {
                      alert("Upload failed: " + err.message);
                    } finally {
                      setUploadingFeatured(false);
                    }
                  };
                  input.click();
                }}
                title="Replace image"
                style={{
                  background: "rgba(0,0,0,0.6)",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  color: "#fff",
                  padding: "4px 8px",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Replace
              </button>
              <button
                onClick={() => setFeaturedImage("")}
                title="Remove image"
                style={{
                  background: "rgba(239,68,68,0.85)",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  color: "#fff",
                  padding: "4px 8px",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/*";
              input.onchange = async () => {
                const file = input.files?.[0];
                if (!file) return;
                setUploadingFeatured(true);
                try {
                  const result = await uploadImage(file);
                  if (result?.url) setFeaturedImage(result.url);
                } catch (err) {
                  alert("Upload failed: " + err.message);
                } finally {
                  setUploadingFeatured(false);
                }
              };
              input.click();
            }}
            disabled={uploadingFeatured}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "20px 24px",
              width: "100%",
              borderRadius: 8,
              border: `2px dashed ${colors.border}`,
              background: "transparent",
              cursor: "pointer",
              color: colors.textSecondary,
              fontSize: 13,
              justifyContent: "center",
            }}
          >
            <ImageIcon size={18} />
            {uploadingFeatured
              ? "Uploading..."
              : "Click to upload a featured image"}
          </button>
        )}
      </div>

      {/* Editor — GrapesJS for L30, TipTap for FGF */}
      {useGrapes ? (
        preview ? (
          <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, overflow: "hidden", background: "#fff" }}>
            <div style={{ padding: "8px 16px", background: `${colors.accent}08`, borderBottom: `1px solid ${colors.border}`, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: colors.accent, fontWeight: 600 }}>
              <Eye size={14} /> Preview Mode
            </div>
            <iframe
              title="Page Preview"
              style={{ width: "100%", height: "70vh", border: "none" }}
              ref={(iframe) => {
                if (!iframe) return;
                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (!doc) return;
                doc.open();
                doc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <script src="https://cdn.tailwindcss.com"></` + `script>
  <script>
    tailwind.config = {
      theme: {
        extend: {
          colors: {
            'deep-teal': '#005A64',
            'dark-teal': '#003D44',
            'liquid-gold': '#D4AF37',
            'terracotta': '#B87333',
            'off-white': '#F5F5F1',
            'graphite': '#333333',
          },
          fontFamily: { sans: ['Inter', 'sans-serif'] },
        },
      },
    };
  </` + `script>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap">
  <style>${gjsDataRef.current.gjsCss || ""}</style>
</head>
<body>${gjsDataRef.current.gjsHtml || gjsDataRef.current.bodyHtml || ""}</body>
</html>`);
                doc.close();
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 16px", background: colors.bgCard, borderTop: `1px solid ${colors.border}`, borderRadius: "0 0 8px 8px", fontSize: 11, color: colors.textSecondary, fontFamily: fonts.body }}>
              {autoSaveStatus && (
                <span style={{ fontSize: 11, color: autoSaveStatus === "error" ? "#ef4444" : autoSaveStatus === "saved" ? "#10b981" : colors.textSecondary }}>
                  {autoSaveStatus === "saving" ? "Saving..." : autoSaveStatus === "saved" ? "Saved" : "Save failed"}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div>
            <Suspense fallback={<div style={{ padding: 40, textAlign: "center", color: colors.textSecondary }}>Loading editor...</div>}>
              <GrapesEditor page={page} onUpdate={handleGrapesUpdate} isLanding={isLanding} />
            </Suspense>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 16px", background: colors.bgCard, borderTop: `1px solid ${colors.border}`, borderRadius: "0 0 8px 8px", fontSize: 11, color: colors.textSecondary, fontFamily: fonts.body }}>
              {autoSaveStatus && (
                <span style={{ fontSize: 11, color: autoSaveStatus === "error" ? "#ef4444" : autoSaveStatus === "saved" ? "#10b981" : colors.textSecondary }}>
                  {autoSaveStatus === "saving" ? "Saving..." : autoSaveStatus === "saved" ? "Saved" : "Save failed"}
                </span>
              )}
            </div>
          </div>
        )
      ) : preview ? (
        <div
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <div
            style={{
              padding: "8px 16px",
              background: `${colors.accent}08`,
              borderBottom: `1px solid ${colors.border}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              color: colors.accent,
              fontWeight: 600,
            }}
          >
            <Eye size={14} /> Preview Mode
          </div>
          <div
            style={{
              padding: "40px 48px",
              maxWidth: 740,
              margin: "0 auto",
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              fontSize: 15,
              lineHeight: 1.7,
              color: "#1a1a1a",
            }}
          >
            <h1 style={{ fontSize: "2rem", fontWeight: 700, marginBottom: 16 }}>
              {title || "Untitled"}
            </h1>
            {featuredImage && (
              <img
                src={featuredImage}
                alt={title || "Featured image"}
                style={{
                  width: "100%",
                  maxHeight: 400,
                  objectFit: "cover",
                  borderRadius: 8,
                  marginBottom: 24,
                }}
              />
            )}
            <div
              dangerouslySetInnerHTML={{ __html: editor?.getHTML() || "" }}
            />
            {isLanding && formConfig?.fields?.length > 0 && (
              <div
                style={{
                  marginTop: 40,
                  padding: 32,
                  background: "#f9fafb",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {formConfig.fields.map((f) => (
                    <div key={f.name}>
                      <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                        {f.label}{f.required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
                      </label>
                      {f.type === "textarea" ? (
                        <textarea
                          disabled
                          placeholder={f.placeholder || ""}
                          style={{
                            width: "100%", padding: "10px 12px", border: "1px solid #d1d5db",
                            borderRadius: 8, fontSize: "0.95rem", fontFamily: "inherit",
                            background: "#fff", minHeight: 80, resize: "vertical", boxSizing: "border-box",
                          }}
                        />
                      ) : f.type === "country" || f.type === "select" ? (
                        <select
                          disabled
                          style={{
                            width: "100%", padding: "10px 12px", border: "1px solid #d1d5db",
                            borderRadius: 8, fontSize: "0.95rem", fontFamily: "inherit",
                            background: "#fff", boxSizing: "border-box",
                          }}
                        >
                          <option>{f.type === "country" ? "Select country..." : "Select..."}</option>
                        </select>
                      ) : f.type === "checkbox" ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input type="checkbox" disabled />
                          <span style={{ fontSize: "0.9rem", color: "#374151" }}>{f.label}</span>
                        </div>
                      ) : (
                        <input
                          type={f.type === "phone" ? "tel" : f.type === "email" ? "email" : "text"}
                          disabled
                          placeholder={f.placeholder || ""}
                          style={{
                            width: "100%", padding: "10px 12px", border: "1px solid #d1d5db",
                            borderRadius: 8, fontSize: "0.95rem", fontFamily: "inherit",
                            background: "#fff", boxSizing: "border-box",
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* GDPR consent preview */}
                <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #e5e7eb", fontSize: "0.82rem", color: "#4b5563", lineHeight: 1.6 }}>
                  <p style={{ marginBottom: 12 }}>
                    FGF is committed to protecting and respecting your privacy, and we'll only use your personal information to administer your account and to provide the products and services you requested from us. From time to time, we would like to contact you about our products and services, as well as other content that may be of interest to you. If you consent to us contacting you for this purpose, please tick below to say how you would like us to contact you:
                  </p>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "16px 0" }}>
                    <input type="checkbox" disabled style={{ marginTop: 3 }} />
                    <span style={{ fontSize: "0.85rem", color: "#374151" }}>
                      I agree to receive other communications from Fordham Global Foresight.<span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>
                    </span>
                  </div>
                  <p style={{ marginBottom: 12 }}>
                    You can unsubscribe from these communications at any time. For more information on how to unsubscribe, our privacy practices, and how we are committed to protecting and respecting your privacy, please review our{" "}
                    <span style={{ color: "#2563eb" }}>Privacy Policy</span>.
                  </p>
                  <p style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                    By clicking submit below, you consent to allow Fordham Global Foresight to store and process the personal information submitted above to provide you the content requested.
                  </p>
                </div>

                <div style={{ marginTop: 20 }}>
                  <button
                    disabled
                    style={{
                      padding: "12px 32px", background: "#2563eb", color: "#fff",
                      border: "none", borderRadius: 8, fontSize: "1rem",
                      fontWeight: 600, cursor: "not-allowed", opacity: 0.7,
                    }}
                  >
                    {formConfig.submitLabel || "Submit"}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "6px 16px",
              background: colors.bgCard,
              borderTop: `1px solid ${colors.border}`,
              fontSize: 11,
              color: colors.textSecondary,
              fontFamily: fonts.body,
            }}
          >
            <div style={{ display: "flex", gap: 16 }}>
              <span>{wordCount} words</span>
              <span>~{Math.max(1, Math.ceil(wordCount / 200))} min read</span>
            </div>
            {autoSaveStatus && (
              <span
                style={{
                  fontSize: 11,
                  color:
                    autoSaveStatus === "error"
                      ? "#ef4444"
                      : autoSaveStatus === "saved"
                        ? "#10b981"
                        : colors.textSecondary,
                }}
              >
                {autoSaveStatus === "saving"
                  ? "Saving..."
                  : autoSaveStatus === "saved"
                    ? "Saved"
                    : "Save failed"}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            border: `2px ${dragging ? "dashed" : "solid"} ${dragging ? "#2563eb" : colors.border}`,
            borderRadius: 8,
            overflow: "hidden",
            transition: "border 0.2s",
            position: "relative",
          }}
        >
          {dragging && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 20,
                background: "rgba(37,99,235,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#2563eb",
                  fontFamily: fonts.body,
                }}
              >
                Drop image(s) here
              </span>
            </div>
          )}
          <EditorToolbar editor={editor} />
          <div style={{ background: "#fff", minHeight: 400, padding: 24 }}>
            <EditorContent editor={editor} />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "6px 16px",
              background: colors.bgCard,
              borderTop: `1px solid ${colors.border}`,
              fontSize: 11,
              color: colors.textSecondary,
              fontFamily: fonts.body,
            }}
          >
            <div style={{ display: "flex", gap: 16 }}>
              <span>{wordCount} words</span>
              <span>~{Math.max(1, Math.ceil(wordCount / 200))} min read</span>
            </div>
            {autoSaveStatus && (
              <span
                style={{
                  fontSize: 11,
                  color:
                    autoSaveStatus === "error"
                      ? "#ef4444"
                      : autoSaveStatus === "saved"
                        ? "#10b981"
                        : colors.textSecondary,
                }}
              >
                {autoSaveStatus === "saving"
                  ? "Saving..."
                  : autoSaveStatus === "saved"
                    ? "Saved"
                    : "Save failed"}
              </span>
            )}
          </div>
        </div>
      )}

      <style>{`
        ${useGrapes ? "" : `
        .tiptap { outline: none; min-height: 360px; color: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 15px; line-height: 1.7; }
        .tiptap h1 { font-size: 2rem; margin: 24px 0 12px; font-weight: 700; }
        .tiptap h2 { font-size: 1.5rem; margin: 20px 0 10px; font-weight: 600; }
        .tiptap h3 { font-size: 1.2rem; margin: 16px 0 8px; font-weight: 600; }
        .tiptap p { margin-bottom: 12px; }
        .tiptap img { max-width: 100%; height: auto; border-radius: 8px; margin: 12px 0; }
        .tiptap blockquote { border-left: 4px solid #e5e7eb; padding: 8px 16px; margin: 12px 0; color: #6b7280; }
        .tiptap a { color: #2563eb; text-decoration: underline; }
        .tiptap ul, .tiptap ol { padding-left: 24px; margin-bottom: 12px; }
        .tiptap hr { border: none; border-top: 2px solid #e5e7eb; margin: 20px 0; }
        .tiptap .is-editor-empty:first-child::before { content: attr(data-placeholder); color: #adb5bd; pointer-events: none; float: left; height: 0; }
        .tiptap table { border-collapse: collapse; margin: 16px 0; width: 100%; }
        .tiptap th, .tiptap td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; min-width: 80px; }
        .tiptap th { background: #f3f4f6; font-weight: 600; font-size: 13px; }
        .tiptap td { font-size: 14px; }
        .tiptap code { background: rgba(0,0,0,0.06); border-radius: 4px; padding: 2px 5px; font-family: 'Fira Code', 'Cascadia Code', Consolas, monospace; font-size: 0.9em; }
        .tiptap pre { background: #1e1e2e; color: #cdd6f4; border-radius: 8px; padding: 16px 20px; margin: 16px 0; overflow-x: auto; font-family: 'Fira Code', 'Cascadia Code', Consolas, monospace; font-size: 13px; line-height: 1.6; }
        .tiptap pre code { background: none; padding: 0; border-radius: 0; font-size: inherit; color: inherit; }
        .tiptap .selectedCell { background: rgba(37,99,235,0.08); }
        `}
      `}</style>

      {/* Lead Capture Form Builder — only for landing pages */}
      {isLanding && formConfig && (
        <div
          style={{
            marginTop: 24,
            padding: 24,
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
          }}
        >
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 16,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FileText size={16} color={colors.accent} />
            Lead Capture Form
          </h3>
          <FormBuilder formConfig={formConfig} onChange={setFormConfig} />
        </div>
      )}

      {/* Submissions — only for existing landing pages */}
      {isLanding && page?.id && (
        <div
          style={{
            marginTop: 24,
            padding: 24,
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: showSubs ? 16 : 0,
            }}
          >
            <h3
              style={{
                fontFamily: fonts.heading,
                fontSize: 16,
                fontWeight: 700,
                color: colors.text,
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              Form Submissions
              {subsTotal > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 10,
                    background: colors.accentDim,
                    color: colors.accent,
                    fontWeight: 700,
                  }}
                >
                  {subsTotal}
                </span>
              )}
            </h3>
            <button
              onClick={() => {
                if (!showSubs) loadSubmissions();
                setShowSubs(!showSubs);
              }}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.textSecondary,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {showSubs ? "Hide" : "View Submissions"}
            </button>
          </div>

          {showSubs && (
            subsLoading ? (
              <div style={{ textAlign: "center", padding: 24, color: colors.textSecondary }}>
                <Loader2 size={18} className="spin" /> Loading...
              </div>
            ) : submissions.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: colors.textSecondary, fontSize: 13 }}>
                No submissions yet
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: "uppercase" }}>Date</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: "uppercase" }}>Email</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: "uppercase" }}>Name</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: "uppercase" }}>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((sub) => (
                      <tr key={sub.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={{ padding: "8px 12px", fontFamily: fonts.mono, fontSize: 12, color: colors.textSecondary, whiteSpace: "nowrap" }}>
                          {fmtDate(sub.submittedAt)}
                        </td>
                        <td style={{ padding: "8px 12px", color: colors.text }}>{sub.contactEmail}</td>
                        <td style={{ padding: "8px 12px", color: colors.text }}>
                          {[sub.data?.firstName, sub.data?.lastName].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td style={{ padding: "8px 12px", color: colors.textSecondary, fontSize: 11, fontFamily: fonts.mono, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {Object.entries(sub.data || {}).filter(([k]) => !["firstName", "lastName", "email"].includes(k)).map(([k, v]) => `${k}: ${v}`).join(", ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────────── */
export default function ContentPages() {
  const tenant = useTenant();
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null); // null = list, "new"/"new-landing" = new page, or page object
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [pageTypeFilter, setPageTypeFilter] = useState("content");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === pages.length) setSelected(new Set());
    else setSelected(new Set(pages.map((p) => p.id)));
  };

  const bulkDelete = async () => {
    const count = selected.size;
    const selectedPages = pages.filter((p) => selected.has(p.id));
    const liveCount = selectedPages.filter((p) => p.pageType === "landing" && p.status === "published").length;
    const msg = liveCount > 0
      ? `⚠️ WARNING: ${liveCount} of these ${count} page${count !== 1 ? "s" : ""} ${liveCount === 1 ? "is a LIVE landing page" : "are LIVE landing pages"} on the public website.\n\nDeleting will immediately break links and stop collecting form submissions.\n\nAre you sure?`
      : `Delete ${count} page${count !== 1 ? "s" : ""}? This cannot be undone.`;
    if (!window.confirm(msg))
      return;
    setBulkLoading(true);
    try {
      await Promise.all([...selected].map((id) => deleteWebPage(id)));
      setSelected(new Set());
      load();
    } catch (err) {
      alert("Some deletes failed: " + err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkSetStatus = async (status) => {
    const count = selected.size;
    const label = status === "published" ? "Publish" : "Unpublish";
    if (!window.confirm(`${label} ${count} page${count !== 1 ? "s" : ""}?`))
      return;
    setBulkLoading(true);
    try {
      const updates = [...selected].map(async (id) => {
        const page = pages.find((p) => p.id === id);
        if (!page) return;
        const full = await fetchWebPage(id);
        await updateWebPage(id, { ...full, status });
      });
      await Promise.all(updates);
      setSelected(new Set());
      load();
    } catch (err) {
      alert("Some updates failed: " + err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  const movePage = async (pageId, direction) => {
    const idx = pages.findIndex((p) => p.id === pageId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= pages.length) return;
    const pageA = pages[idx];
    const pageB = pages[swapIdx];
    const orderA = typeof pageA.sortOrder === "number" ? pageA.sortOrder : idx;
    const orderB =
      typeof pageB.sortOrder === "number" ? pageB.sortOrder : swapIdx;
    try {
      await Promise.all([
        updateWebPage(pageA.id, { ...pageA, sortOrder: orderB }),
        updateWebPage(pageB.id, { ...pageB, sortOrder: orderA }),
      ]);
      load();
    } catch (err) {
      alert("Reorder failed: " + err.message);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const result = await fetchWebPages({ status: statusFilter, search, pageType: pageTypeFilter });
      setPages(result.pages || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Failed to load pages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter, search, pageTypeFilter]);

  const handleDelete = async (page) => {
    const isPublishedLanding = page.pageType === "landing" && page.status === "published";
    const msg = isPublishedLanding
      ? `⚠️ WARNING: "${page.title}" is a LIVE landing page on the public website.\n\nDeleting it will immediately break any links pointing to this page and stop collecting form submissions.\n\nAre you sure you want to delete it?`
      : page.pageType === "landing"
        ? `Delete landing page "${page.title}"? This will remove the page and all its form submissions. This cannot be undone.`
        : `Delete "${page.title}"? This cannot be undone.`;
    if (!window.confirm(msg))
      return;
    try {
      await deleteWebPage(page.id);
      load();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handleEdit = async (page) => {
    try {
      const full = await fetchWebPage(page.id);
      setEditing(full);
    } catch (err) {
      alert("Failed to load page: " + err.message);
    }
  };

  const handleSave = async (data) => {
    const silent = data._silent;
    const cleanData = { ...data };
    delete cleanData._silent;
    if (!silent) setSaving(true);
    try {
      if (editing === "new" || (typeof editing === "string" && editing.startsWith("new-landing"))) {
        if (typeof editing === "string" && editing.startsWith("new-landing")) {
          cleanData.pageType = "landing";
          if (!cleanData.formConfig) {
            const tplId = editing.replace("new-landing:", "") || undefined;
            cleanData.formConfig = getDefaultFormConfig(tplId);
          }
        }
        await createWebPage(cleanData);
        setEditing(null);
        load();
      } else {
        await updateWebPage(editing.id, cleanData);
        if (!silent) {
          setEditing(null);
          load();
        }
      }
      // Trigger static site publish for L30 tenant when a page is published
      if (tenant.id === "l30" && cleanData.status === "published" && !silent) {
        publishStatic().catch((err) =>
          console.warn("Static publish failed (site will use API fallback):", err.message),
        );
      }
    } catch (err) {
      if (!silent) alert(err.message);
      throw err; // re-throw so autosave can catch it
    } finally {
      if (!silent) setSaving(false);
    }
  };

  // ── Editor view ──
  if (editing) {
    const isNewLanding = typeof editing === "string" && editing.startsWith("new-landing");
    const editorPage =
      editing === "new" ? null
        : isNewLanding ? { pageType: "landing", formConfig: getDefaultFormConfig(editing.replace("new-landing:", "") || undefined) }
          : editing;
    return (
      <div style={{ padding: 32, maxWidth: 960, margin: "0 auto" }}>
        <PageEditor
          page={editorPage}
          onSave={handleSave}
          onBack={() => setEditing(null)}
          saving={saving}
        />
      </div>
    );
  }

  // ── List view ──
  const published = pages.filter((p) => p.status === "published").length;
  const drafts = pages.filter((p) => p.status === "draft").length;

  const statCards = [
    { label: "Total", value: total, color: colors.accent },
    { label: "Published", value: published, color: "#10b981" },
    { label: "Drafts", value: drafts, color: "#94a3b8" },
  ];

  return (
    <div
      style={{
        padding: 32,
        maxWidth: 1100,
        margin: "0 auto",
        paddingBottom: 60,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 28,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: colors.text,
              fontFamily: fonts.heading,
              marginBottom: 4,
            }}
          >
            {pageTypeFilter === "landing" ? "Landing Pages" : "Content Pages"}
          </h1>
          <p style={{ fontSize: 13, color: colors.textSecondary }}>
            {pageTypeFilter === "landing" ? "Lead capture pages with forms" : "Manage website pages and content"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            onClick={() => setEditing("new")}
            variant={pageTypeFilter === "landing" ? "default" : "primary"}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} /> New Page
          </Button>
          <Button
            onClick={() => setShowTemplatePicker(true)}
            variant={pageTypeFilter === "landing" ? "primary" : "default"}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} /> New Landing Page
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {statCards.map((s) => (
          <div
            key={s.label}
            style={{
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: "16px 20px",
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              {s.label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Page type tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[
          { id: "content", label: "Content Pages" },
          { id: "landing", label: "Landing Pages" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => { setPageTypeFilter(t.id); setSelected(new Set()); }}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: `1px solid ${pageTypeFilter === t.id ? colors.accent : colors.border}`,
              background: pageTypeFilter === t.id ? colors.accentDim : "transparent",
              color: pageTypeFilter === t.id ? colors.accent : colors.textSecondary,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}
      >
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search
            size={16}
            style={{
              position: "absolute",
              left: 12,
              top: 11,
              color: colors.textSecondary,
            }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pages..."
            style={{
              width: "100%",
              padding: "9px 14px 9px 36px",
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.bg,
              color: colors.text,
              fontSize: 14,
              outline: "none",
            }}
          />
        </div>
        {["", "draft", "published"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: `1px solid ${statusFilter === s ? colors.accent : colors.border}`,
              background:
                statusFilter === s ? `${colors.accent}15` : "transparent",
              color: statusFilter === s ? colors.accent : colors.textSecondary,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 16px",
            marginBottom: 16,
            background: `${colors.accent}10`,
            border: `1px solid ${colors.accent}30`,
            borderRadius: 10,
            fontSize: 13,
            fontFamily: fonts.body,
          }}
        >
          <CheckSquare size={16} color={colors.accent} />
          <span style={{ color: colors.text, fontWeight: 600 }}>
            {selected.size} selected
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => bulkSetStatus("published")}
            disabled={bulkLoading}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "1px solid #10b981",
              background: "rgba(16,185,129,0.1)",
              color: "#10b981",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Eye size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
            Publish
          </button>
          <button
            onClick={() => bulkSetStatus("draft")}
            disabled={bulkLoading}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              background: "transparent",
              color: colors.textSecondary,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <EyeOff size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
            Unpublish
          </button>
          <button
            onClick={bulkDelete}
            disabled={bulkLoading}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "1px solid rgba(239,68,68,0.4)",
              background: "rgba(239,68,68,0.08)",
              color: "#ef4444",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Trash2 size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
            Delete
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: 60,
            color: colors.textSecondary,
          }}
        >
          <Loader2 size={24} className="spin" />        </div>
      ) : pages.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: 60,
            color: colors.textSecondary,
          }}
        >
          <FileText size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontSize: 15 }}>No pages yet</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>
            Create your first page to get started
          </p>
        </div>
      ) : (
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={{ padding: "12px 8px 12px 16px", width: 36 }}>
                  <input
                    type="checkbox"
                    checked={pages.length > 0 && selected.size === pages.length}
                    onChange={toggleSelectAll}
                    style={{ cursor: "pointer", accentColor: colors.accent }}
                  />
                </th>
                {["Title", "Slug", "Status", "Updated", "Order", ""].map(
                  (h) => (
                    <th
                      key={h}
                      style={{
                        padding: "12px 16px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: colors.textSecondary,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        textAlign: "left",
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr
                  key={page.id}
                  onClick={() => handleEdit(page)}
                  style={{
                    borderBottom: `1px solid ${colors.border}`,
                    cursor: "pointer",
                    transition: "background 0.15s",
                    background: selected.has(page.id)
                      ? `${colors.accent}08`
                      : "transparent",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = selected.has(page.id)
                      ? `${colors.accent}12`
                      : colors.bgHover)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = selected.has(page.id)
                      ? `${colors.accent}08`
                      : "transparent")
                  }
                >
                  <td style={{ padding: "12px 8px 12px 16px", width: 36 }}>
                    <input
                      type="checkbox"
                      checked={selected.has(page.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelect(page.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: "pointer", accentColor: colors.accent }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: 14,
                      fontWeight: 500,
                      color: colors.text,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {page.title}
                      {page.pageType === "landing" && (
                        <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: "rgba(99,102,241,0.08)", color: "#6366f1", fontWeight: 700 }}>
                          FORM
                        </span>
                      )}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: 13,
                      color: colors.textSecondary,
                    }}
                  >
                    /page/{page.slug}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <StatusBadge status={page.status} />
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: 13,
                      color: colors.textSecondary,
                    }}
                  >
                    {fmtDate(page.updatedAt)}
                  </td>
                  <td style={{ padding: "12px 8px", textAlign: "center" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          movePage(page.id, "up");
                        }}
                        disabled={pages.indexOf(page) === 0}
                        title="Move up"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 2,
                          color:
                            pages.indexOf(page) === 0
                              ? colors.border
                              : colors.textSecondary,
                          display: "flex",
                        }}
                      >
                        <ArrowUpCircle size={15} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          movePage(page.id, "down");
                        }}
                        disabled={pages.indexOf(page) === pages.length - 1}
                        title="Move down"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 2,
                          color:
                            pages.indexOf(page) === pages.length - 1
                              ? colors.border
                              : colors.textSecondary,
                          display: "flex",
                        }}
                      >
                        <ArrowDownCircle size={15} />
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        justifyContent: "flex-end",
                      }}
                    >
                      {page.status === "published" && (
                        <a
                          href={`/api/page/${page.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="View public page"
                          style={{
                            color: colors.textSecondary,
                            padding: 4,
                            display: "flex",
                          }}
                        >
                          <ExternalLink size={15} />
                        </a>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(page);
                        }}
                        title="Delete page"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: colors.textSecondary,
                          padding: 4,
                          display: "flex",
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Landing Page Template Picker Modal */}
      {showTemplatePicker && (
        <div
          onClick={() => setShowTemplatePicker(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: colors.bgCard,
              borderRadius: 16,
              padding: 32,
              width: "100%",
              maxWidth: 520,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 20,
                fontWeight: 700,
                color: colors.text,
                marginBottom: 8,
              }}
            >
              Choose a Template
            </h2>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 24 }}>
              Select a landing page template to get started
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {LANDING_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={() => {
                    setShowTemplatePicker(false);
                    setEditing(`new-landing:${tpl.id}`);
                    setPageTypeFilter("landing");
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "16px 20px",
                    background: colors.bgMain,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 12,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.accent;
                    e.currentTarget.style.background = colors.accentDim;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.background = colors.bgMain;
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: colors.text, marginBottom: 4 }}>
                      {tpl.label}
                    </div>
                    <div style={{ fontSize: 12, color: colors.textSecondary }}>
                      {tpl.description}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      padding: "3px 10px",
                      borderRadius: 10,
                      background: colors.accentDim,
                      color: colors.accent,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tpl.fields.length} fields
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowTemplatePicker(false)}
              style={{
                marginTop: 16,
                width: "100%",
                padding: "10px",
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                background: "transparent",
                color: colors.textSecondary,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
